import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Paper,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  VolumeUp,
  VolumeOff,
  VolumeDown
} from '@mui/icons-material';
import { audioPreloader } from '../../services/audioPreloader';

interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
  autoPlay?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

// Audio loading states for better state management
type AudioLoadingState = 'loading' | 'ready' | 'error' | 'idle';

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  title = 'Audio Question',
  autoPlay = false,
  onPlay,
  onPause,
  onEnded
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [loadingState, setLoadingState] = useState<AudioLoadingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get cached audio URL if available
  const actualAudioUrl = useMemo(() => {
    if (!audioUrl) return '';
    return audioPreloader.getCachedAudioUrl(audioUrl);
  }, [audioUrl]);

  // Cleanup function to properly reset audio state
  const resetAudioState = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      // Stop playback and reset position
      audio.pause();
      audio.currentTime = 0;
    }

    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset all state
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoadingState('idle');
    setError(null);
    setIsSeeking(false);
  }, []);

  // Reset all state when audioUrl changes (new question)
  useEffect(() => {
    resetAudioState();

    // Set loading state for new audio
    if (audioUrl) {
      // Check if audio is already cached
      if (audioPreloader.isCached(audioUrl)) {
        setLoadingState('loading');
        // Shorter timeout for cached audio
        timeoutRef.current = setTimeout(() => {
          setLoadingState('error');
          setError('Cached audio failed to load.');
        }, 2000); // 2 second timeout for cached audio
      } else {
        setLoadingState('loading');
        // Longer timeout for CDN audio
        timeoutRef.current = setTimeout(() => {
          setLoadingState('error');
          setError('Audio loading timeout. Please check your connection.');
        }, 15000); // 15 second timeout for CDN audio
      }
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [actualAudioUrl, resetAudioState]);

  // Set up audio event listeners with proper cleanup
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !actualAudioUrl) return;

    const handleLoadedMetadata = () => {

      // Clear loading timeout since audio loaded successfully
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setDuration(audio.duration);
      setLoadingState('ready');
      setError(null);
    };

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlay?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEnded?.();
    };

    const handleError = (e: Event) => {

      // Clear loading timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setError('Failed to load audio file. Please check your connection.');
      setLoadingState('error');
    };

    const handleCanPlay = () => {
      // Don't set loading state here to avoid race condition
      // Let loadedmetadata handle the ready state
    };

    const handleSeeked = () => {
      setIsSeeking(false);
    };

    const handleLoadStart = () => {
      setLoadingState('loading');
    };

    // Add all event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('seeked', handleSeeked);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      // Clean up all event listeners
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('seeked', handleSeeked);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [actualAudioUrl, onPlay, onPause, onEnded, isSeeking]); // Include actualAudioUrl in dependencies

  // Update volume when volume or mute state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      audio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volume));
    } catch (error) {
    }
  }, [volume, isMuted]);

  // Auto-play if requested (only when audio is ready)
  useEffect(() => {
    if (autoPlay && audioRef.current && loadingState === 'ready') {
      audioRef.current.play().catch((error) => {
        // Don't set error state for auto-play failures as they're often expected
      });
    }
  }, [autoPlay, loadingState]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || loadingState !== 'ready') return;

    if (isPlaying) {
      try {
        audio.pause();
      } catch (error) {
      }
    } else {
      audio.play().catch((err) => {
        setError('Failed to play audio. Please try again.');
      });
    }
  }, [isPlaying, loadingState]);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
    } catch (error) {
    }
  }, []);

  const handleSeek = useCallback((event: Event, newValue: number | number[]) => {
    const audio = audioRef.current;
    if (!audio || loadingState !== 'ready' || duration === 0) return;

    const seekTime = Array.isArray(newValue) ? newValue[0] : newValue;
    const clampedSeekTime = Math.max(0, Math.min(duration, seekTime));

    setIsSeeking(true);
    setCurrentTime(clampedSeekTime);

    try {
      audio.currentTime = clampedSeekTime;
    } catch (error) {
      setIsSeeking(false);
    }
  }, [duration, loadingState]);

  const handleVolumeChange = useCallback((event: Event, newValue: number | number[]) => {
    const newVolume = Array.isArray(newValue) ? newValue[0] : newValue;
    const clampedVolume = Math.max(0, Math.min(1, newVolume));

    setVolume(clampedVolume);
    setIsMuted(clampedVolume === 0);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Memoize formatTime function to prevent unnecessary re-renders
  const formatTime = useMemo(() => {
    return (time: number) => {
      if (!isFinite(time) || time < 0) return '0:00';
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };
  }, []);

  // Memoize volume icon to prevent unnecessary re-renders
  const VolumeIcon = useMemo(() => {
    if (isMuted || volume === 0) return VolumeOff;
    if (volume < 0.5) return VolumeDown;
    return VolumeUp;
  }, [isMuted, volume]);

  // Show error state
  if (loadingState === 'error' && error) {
    return (
      <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Paper>
    );
  }

  // Don't render if no audio URL
  if (!audioUrl) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 2, bgcolor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
          <IconButton
            onClick={toggleMute}
            size="small"
            aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
          >
            <VolumeIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Loading indicator */}
      {loadingState === 'loading' && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Loading audio...
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <IconButton
            onClick={togglePlay}
            size="large"
            disabled={loadingState !== 'ready'}
            aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.400', color: 'grey.600' }
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Stop">
          <IconButton
            onClick={stopAudio}
            size="small"
            disabled={loadingState !== 'ready'}
            aria-label="Stop audio"
          >
            <Stop />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1, mx: 2 }}>
          <Slider
            value={currentTime}
            max={duration > 0 ? duration : 1}
            onChange={handleSeek}
            disabled={loadingState !== 'ready' || duration === 0}
            aria-label="Audio progress"
            sx={{ color: 'primary.main' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatTime(currentTime)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loadingState === 'ready' ? formatTime(duration) : '--:--'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: 100 }}>
          <Tooltip title="Volume">
            <Slider
              value={isMuted ? 0 : volume}
              max={1}
              step={0.1}
              onChange={handleVolumeChange}
              aria-label="Volume control"
              sx={{ color: 'primary.main' }}
            />
          </Tooltip>
        </Box>
      </Box>

      <audio
        ref={audioRef}
        src={actualAudioUrl}
        preload="metadata"
        crossOrigin="anonymous"
        aria-hidden="true"
      />
    </Paper>
  );
};

export default AudioPlayer; 